/* eslint-disable max-lines */
import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue"
import AllPortals from "@/components/GetPortals/PortalsComponent.vue"
import Column from "@/components/Column/Column.vue"
import { get_token, get_user_details } from "@/router";
import axios from "axios";
import draggable from 'vuedraggable';
import { get_tag_style } from "@/utils/task_tags";
import {
  connect_portal_realtime_socket,
  disconnect_portal_realtime_socket,
} from "@/services/realtime";

function build_board_columns_payload(board_id, filters) {
  return {
    board_id,
    search: filters.search,
    assignee_ids: filters.users,
    statuses: filters.statuses,
    due_date: filters.dueDate,
    due_month: filters.dueMonth,
    tag_ids: filters.tags
  };
}

function build_auth_headers_config(token) {
  const headers = {};
  headers.Authorization = `Token ${token}`;
  headers["Content-Type"] = "application/json";
  return Object.assign(Object.create(null), { headers });
}

export default {
  name: 'Board',
  components: {
    draggable,
    AllPortals,
    Column,
    HomeNavBar

  },
  data() {
    return ({
      buckets: [],
      isAddingBucket: false,
      newBucketTitle: '',
      members: [],
      board_loading:true,
      filterDropdownOpen: false,
      availableStatuses: [],
      availablePriorities: [],
      availableTags: [],
      filters: {
        search: "",
        users: [],
        statuses: [],
        dueDate: "",
        dueMonth: "",
        tags: [],
      },
      showAllMembers: false,
      showAllStatuses: false,
      portals_open: true,
      portal_admin: false,
      localTaskUpdateExpirations: {},
      user_details: get_user_details() || { user_role: '' }
});
  },

  computed: {
    can_manage_task_statuses() {
      return this.user_details?.user_role === 'admin' || this.portal_admin;
    },
    activeFilterCount() {
      return this.filters.users.length
        + this.filters.statuses.length
        + this.filters.tags.length
        + (this.filters.search ? 1 : 0)
        + (this.filters.dueDate ? 1 : 0)
        + (this.filters.dueMonth ? 1 : 0);
    },
    filteredTaskCount() {
      return this.buckets.reduce((total, bucket) => total + (bucket?.tasks?.length || 0), 0);
    },
  },
  watch:{
    '$route.params.portal_id'() {
      this.connect_portal_realtime();
      this.fetch_board_columns({ silent: true });
    },
    '$route.params.board_id'() {
      this.filters.tags = [];
      this.fetch_tags();
      this.fetch_board_columns();
    },
    'filters.users': {
      deep: true,
      handler() {
        this.fetch_board_columns();
      }
    },
    'filters.statuses': {
      deep: true,
      handler() {
        this.fetch_board_columns();
      }
    },
    'filters.tags': {
      deep: true,
      handler() { this.fetch_board_columns(); }
    },
    'filters.dueDate'() {
      if (this.filters.dueDate) {
        this.filters.dueMonth = "";
      }
      this.fetch_board_columns();
    },
    'filters.dueMonth'() {
      if (this.filters.dueMonth) {
        this.filters.dueDate = "";
      }
      this.fetch_board_columns();
    },
    'filters.search'() {
      this.fetch_board_columns();
    }
  },
created() {
  this.fetch_Portal_admin();
  this.fetch_task_statuses();
  this.fetch_priority_statuses();
  this.fetch_tags();
  this.fetch_board_columns().then(() => {
    this.fetchPortalMembers();
  });
  
},
mounted() {
  this.connect_portal_realtime();
  this._realtime_board_handler = (event) => this.handle_realtime_board(event);
  window.addEventListener("realtime:portal-boards", this._realtime_board_handler);
},
beforeUnmount() {
  if (this._realtime_board_handler) {
    window.removeEventListener("realtime:portal-boards", this._realtime_board_handler);
    this._realtime_board_handler = null;
  }
  disconnect_portal_realtime_socket();
},
  methods: {
    connect_portal_realtime() {
      if (!this.$route.params.portal_id || !this.getToken()) return;
      connect_portal_realtime_socket(this.$route.params.portal_id);
    },
    handle_realtime_board(event) {
      const data = event?.detail?.data || {};
      if (String(data.portal_id) !== String(this.$route.params.portal_id)) return;
      if (String(data.board_id) !== String(this.$route.params.board_id)) return;

      const taskId = String(data.task_id || '');
      const localExpiration = Reflect.get(this.localTaskUpdateExpirations, taskId);
      if (taskId && localExpiration && localExpiration > Date.now()) {
        Reflect.deleteProperty(this.localTaskUpdateExpirations, taskId);
        return;
      }

      this.fetch_board_columns();
    },
    register_local_task_update(taskId) {
      const key = String(taskId || '');
      if (!key) return;
      Reflect.set(this.localTaskUpdateExpirations, key, Date.now() + 10000);
      window.setTimeout(() => {
        if ((Reflect.get(this.localTaskUpdateExpirations, key) || 0) <= Date.now()) {
          Reflect.deleteProperty(this.localTaskUpdateExpirations, key);
        }
      }, 10100);
    },

    format_label(text) {
      if (!text) return '';
      return text
        .trim()
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },
    get_member_initials(member) {
      const name = String(member?.full_name || member?.username || member?.name || "").trim();
      if (!name) return "?";

      return name
        .split(/\s+/)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
    chip_palette_class(seed, prefix = 'task-chip-tone') {
      const value = String(seed || '').trim();
      const hash = value.split('').reduce((total, char) => total + char.charCodeAt(0), 0);
      return `${prefix}-${hash % 8}`;
    },
    status_chip_class(status) {
      return this.chip_palette_class(status?.id || status?.task_name || status?.name || status?.label);
    },
    getToken() {
      return get_token();
    },
    get_user_id() {
      return localStorage.getItem('user_id') || '';
    },
    normalize_board_columns(columns = []) {
      return columns.map(c => ({
        ...c,
        tasks: c.tasks || [],
        tasks_pagination: c.tasks_pagination || { current_page: 1, total_pages: 1, total_tasks: c.tasks?.length || 0 }
      }));
    },
    get_board_columns_payload() {
      return build_board_columns_payload(this.$route.params.board_id, this.filters);
    },
    get_auth_headers_config() {
      return build_auth_headers_config(this.getToken());
    },
    set_board_loading_for_fetch(silent) {
      const previous_board_loading = this.board_loading;
      if (!silent) this.board_loading = true;
      return previous_board_loading;
    },
    restore_board_loading_after_fetch(silent, previous_board_loading) {
      this.board_loading = silent ? previous_board_loading : false;
    },
    apply_board_columns_response(response) {
      if (response.data.status !== "success") return;
      this.buckets = this.normalize_board_columns(response.data.data);
    },
    fetch_Portal_admin() {
      const user_id = Number(this.get_user_id());

      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_plartform_admin_api/`,
        method: 'POST',
        headers: {
          'Authorization': `Token ${this.getToken()}`,
          'Content-Type': 'application/json'
        },
        data: JSON.stringify({
          portal_id: this.$route.params.portal_id,
          user_id
        }),
        success: (response) => {
          if (response.status === 'success') {
            const admins = response?.portal_admin_detalails || [];
            this.portal_admin = admins.some((admin) => Number(admin?.user?.id) === user_id);
            return;
          }
          this.portal_admin = false;
        },
        error: () => {
          this.portal_admin = false;
        }
      });
    },
    goBack() {
      this.$router.back();
    },

    toggleUserFilter(userId) {
      const index = this.filters.users.indexOf(userId);
      if (index === -1) {
        // Not in the array yet, so add it
        this.filters.users.push(userId);
      } else {
        // Already in the array, so remove it (un-click)
        this.filters.users.splice(index, 1);
      }
    },

    toggleStatusFilter(statusId) {
      const index = this.filters.statuses.indexOf(statusId);
      if (index === -1) {
        this.filters.statuses.push(statusId);
      } else {
        this.filters.statuses.splice(index, 1);
      }
    },
    tag_style: get_tag_style,
    toggleTagFilter(tagId) {
      const index = this.filters.tags.indexOf(tagId);
      if (index === -1) this.filters.tags.push(tagId);
      else this.filters.tags.splice(index, 1);
    },
    remove_deleted_task_tag(tagId) {
      const deletedTagId = Number(tagId);
      this.availableTags = this.availableTags.filter(tag => Number(tag.id) !== deletedTagId);
      this.filters.tags = this.filters.tags.filter(id => Number(id) !== deletedTagId);
      this.buckets.forEach(bucket => {
        (bucket.tasks || []).forEach(task => {
          task.tags = (task.tags || []).filter(tag => Number(tag.id) !== deletedTagId);
        });
      });
    },
    clearDueDateFilter() {
      this.filters.dueDate = "";
    },
    clearDueMonthFilter() {
      this.filters.dueMonth = "";
    },
clearAllFilters() {
  this.filters = { search: "", users: [], statuses: [], tags: [], dueDate: "", dueMonth: "" };
},

    fetch_tags() {
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_board_task_tags/`,
        method: 'POST',
        headers: { 'Authorization': `Token ${this.getToken()}`, 'Content-Type': 'application/json' },
        data: JSON.stringify({ board_id: this.$route.params.board_id }),
        success: (response) => { this.availableTags = response.data || []; }
      });
    },


    fetch_task_statuses() {
     $.ajax({
          url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_task_statuses/`,
          method: 'POST',
          headers: {
          'Authorization': `Token ${this.getToken()}`,
          'Content-Type': 'application/json'
          },
          success: (response) => {
          var status = response.status
          if (status == "success") {
              this.availableStatuses = response.data;
          }

          },

      });

    },
  fetch_priority_statuses() {
    $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_priority_statuses/`,
        method: 'POST',
        headers: {
            'Authorization': `Token ${this.getToken()}`,
            'Content-Type': 'application/json'
        },
        success: (response) => {
            var status = response.status
            if (status == "success") {
                this.availablePriorities = response.data
            }
        }
    });
  },

  on_column_drag_change(evt) {
  if (!evt || !evt.moved) return;

  const movedColumn = this.buckets[evt.moved.newIndex];
  if (!movedColumn) return;

  const board_id = this.$route.params.board_id; // ✅ manually reference board id
  const column_id = movedColumn.id;
  const new_position = evt.moved.newIndex + 1;

  this.moveColumn(board_id, column_id, new_position);
},


moveColumn(board_id, column_id, new_position) {
  $.ajax({
    url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/move_board_column_position/`,
    method: "POST",
    data: JSON.stringify({
      board_id: board_id,
      column_id: column_id,
      new_position: new_position,
    }),
    headers: {
      Authorization: `Token ${this.getToken()}`,
      "Content-Type": "application/json",
    },
  });
},
    async fetch_board_columns(options = {}) {
      const silent = options?.silent === true;
      const previous_board_loading = this.set_board_loading_for_fetch(silent);

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_board_columns/`,
          this.get_board_columns_payload(),
          this.get_auth_headers_config()
        );

        this.apply_board_columns_response(response);
        return response;
      } catch (error) {
        console.error("Error fetching board columns:", error);
        throw error;
      } finally {
        this.restore_board_loading_after_fetch(silent, previous_board_loading);
      }
    },
    add_new_column() {
      this.isAddingBucket = true;
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/create_column/`,
        method: 'POST',
        headers: {
          'Authorization': `Token ${this.getToken()}`,
          'Content-Type': 'application/json'
        },
        data: JSON.stringify({ board_id: this.$route.params.board_id, name: this.newBucketTitle }),
        success: (response) => {
          var status = response.status
          if (status == "success") {
            this.buckets.push(response.data)
            this.newBucketTitle = '';
            this.isAddingBucket = false;
          }

        },

      });
    },
    startAddingBucket() {
      this.isAddingBucket = true;
      this.newBucketTitle = '';
      this.$nextTick(() => {
        this.$refs.newBucketInput?.focus();
      });
    },
    deleteBucket(index) {
      this.buckets.splice(index, 1);
    },
    fetchPortalMembers() {
      this.members = [];

      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_portal_members/`,
        method: "GET",
        data: { portal_id: this.$route.params.portal_id },
        headers: {
          "Authorization": `Token ${this.getToken()}`,
          "Content-Type": "application/json"
        },
        success: (response) => {
          if (response.status === "success") {
            this.members = response.data.members;
          }
        },
        error: (xhr) => {
          console.error("Error fetching portal members:", xhr);
        }
      });
    },
  }
};
